<template>
  <div class="pb-16">
    <div class="pt-3">
      <div class="mb-4 flex items-end justify-between gap-3">
        <div class="min-w-0">
          <div class="mb-2 flex items-center gap-2">
            <span class="inline-flex h-5 items-center rounded-full border border-brand-green/25 bg-brand-green/10 px-2 text-[9px] font-black tracking-[0.14em] text-brand-green">
              NFL
            </span>
            <span class="text-[9px] font-bold uppercase tracking-[0.16em] text-slate-400">
              Regular season
            </span>
          </div>
          <div class="flex items-center gap-3">
            <h1 class="text-[28px] font-black leading-none tracking-tight text-white sm:text-4xl">
              Week <span class="text-brand-green">{{ week || "—" }}</span>
            </h1>
            <!-- Re-reads the week now: the picks request pulls fresh ESPN
                 scores and grades anything that just went final. Free -- no
                 Odds API call, unlike Home's admin refresh. -->
            <button
              type="button"
              class="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-white/10 bg-white/[0.04] text-slate-400 transition-colors hover:bg-white/[0.09] hover:text-slate-200 disabled:opacity-50"
              :disabled="refreshing || week == null"
              :aria-label="refreshing ? 'Fetching latest scores' : 'Fetch latest scores'"
              :title="refreshing ? 'Fetching latest scores…' : 'Fetch latest scores'"
              @click="refreshNow"
            >
              <svg
                class="h-4 w-4"
                :class="{ 'animate-spin': refreshing }"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M21 12a9 9 0 1 1-2.64-6.36" />
                <polyline points="21 3 21 9 15 9" />
              </svg>
            </button>
          </div>
          <p class="mt-2 text-xs font-medium text-slate-400 sm:text-sm">
            Review your five picks against the frozen line.
          </p>
        </div>

        <!-- Week navigation lives with the week heading instead of repeating
             the selected week in a second row underneath it. -->
        <div class="flex shrink-0 items-center gap-1 rounded-xl border border-white/10 bg-brand-navy/40 p-1 shadow-sm shadow-black/10">
          <button
            type="button"
            :disabled="!canGoPrevWeek"
            @click="goToWeek(-1)"
            class="flex h-9 w-9 items-center justify-center rounded-lg text-xl font-semibold text-slate-300 transition-colors hover:bg-white/[0.06] hover:text-white disabled:opacity-25"
            aria-label="Previous week"
          >
            ‹
          </button>
          <span class="h-5 w-px bg-white/10" aria-hidden="true"></span>
          <button
            type="button"
            :disabled="!canGoNextWeek"
            @click="goToWeek(1)"
            class="flex h-9 w-9 items-center justify-center rounded-lg text-xl font-semibold text-slate-300 transition-colors hover:bg-white/[0.06] hover:text-white disabled:opacity-25"
            aria-label="Next week"
          >
            ›
          </button>
        </div>
      </div>
    </div>

    <p
      v-if="error"
      class="mb-4 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-300"
    >
      {{ error }}
    </p>
    <p
      v-if="!loading && !error && pickError"
      class="mb-4 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-300"
    >
      {{ pickError }}
    </p>

    <!-- Loading -->
    <div v-if="loading" class="flex flex-col items-center justify-center py-20">
      <div
        class="h-10 w-10 animate-spin rounded-full border-4 border-slate-700 border-t-emerald-500"
      ></div>
      <p class="mt-4 text-base text-slate-400">Loading this week's picks…</p>
    </div>

    <!-- Your place in the pool as this week's results land, once the
         commissioner's All Picks sheet is in and your account is linked. -->
    <PoolStandingCard
      v-if="!loading && poolStandings && poolStandings.me"
      :standings="poolStandings"
      :baseline-week="crowd.baseline_week"
    />

    <!-- Once any of your picks has kicked off: live lanes, then up next,
         then final -- and five tiles once the week is settled. -->
    <GamedayPicks
      v-if="!loading && gameday.length && gamedaySummary.started"
      :picks="gameday"
      :summary="gamedaySummary"
    />

    <!-- Before kickoff, the review-and-send card. No picks: nothing here. The
         games list below is the page then, and an empty-state block only
         pushed it a screen down. -->
    <section
      v-else-if="!loading && pickedGames.length"
      class="mb-4 rounded-2xl border border-white/10 bg-brand-navy/40 p-2.5 shadow-lg shadow-black/15 sm:p-3"
      aria-labelledby="review-picks-heading"
    >
      <header class="mb-2.5 flex items-center justify-between gap-4 px-0.5">
        <div>
          <div class="flex items-baseline gap-2">
            <h2 id="review-picks-heading" class="text-sm font-extrabold text-white">
              Your Picks
            </h2>
            <span
              v-if="overPickLimit"
              class="text-[10px] font-bold uppercase tracking-[0.1em] text-rose-400"
            >
              Trim to 5
            </span>
            <span
              v-else-if="allSubmitted"
              class="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-300"
            >
              Locked
            </span>
            <span
              v-else-if="atPickLimit && !sendingDone"
              class="text-[10px] font-bold uppercase tracking-[0.1em] text-brand-green"
            >
              Ready to send
            </span>
          </div>
          <p class="mt-0.5 text-[10px]" :class="overPickLimit ? 'text-rose-400' : 'text-slate-400'">
            <template v-if="overPickLimit">Remove {{ picksMade - 5 }} extra pick<span v-if="picksMade - 5 !== 1">s</span>.</template>
            <template v-else-if="allSubmitted">On the commissioner's sheet.</template>
            <template v-else-if="sendingDone">Games are under way.</template>
            <template v-else-if="atPickLimit">Review each line before emailing.</template>
            <template v-else>{{ 5 - picksMade }} pick<span v-if="5 - picksMade !== 1">s</span> remaining.</template>
          </p>
        </div>

        <div class="shrink-0">
          <p class="mb-1 text-right text-xs font-black tabular-nums text-slate-300">
            <span :class="overPickLimit ? 'text-rose-400' : 'text-brand-green'">{{ picksMade }}</span>/5
          </p>
          <div class="flex items-center gap-1" role="img" :aria-label="`${picksMade} of 5 picks made`">
            <span
              v-for="slot in 5"
              :key="slot"
              class="h-1.5 w-5 rounded-full"
              :class="slot <= picksMade ? 'bg-brand-green' : 'bg-white/10'"
            ></span>
            <span v-if="overPickLimit" class="text-[10px] font-black text-rose-400">
              +{{ picksMade - 5 }}
            </span>
          </div>
        </div>
      </header>

      <div>
        <div
          v-for="(game, index) in pickedGames"
          :key="game.id"
          class="border-b border-white/[0.07] last:border-b-0"
        >
          <PickCard
            :game="game"
            :position="index + 1"
            :over-limit="overPickLimit"
            @remove="removePick(game)"
          />
        </div>
      </div>
    </section>

    <!-- Review first, then send. The app opens a pre-filled Gmail draft but
         never sends it; the picks lock themselves when the commissioner's All
         Picks email lists them (Pool::MarkSubmittedPicks). Gone once there is
         nothing left to send -- the header's "Locked" says the rest. -->
    <div
      v-if="!loading && pickedGames.length && !sendingDone && !sendDeadlinePassed"
      class="rounded-xl border border-slate-700/60 bg-slate-800/40 p-3"
    >
      <div class="flex items-center justify-between gap-3">
        <div class="min-w-0">
          <p class="text-sm font-semibold text-white">Send to the commissioner</p>
          <p class="mt-0.5 text-xs" :class="overPickLimit ? 'text-rose-400' : 'text-slate-400'">
            <template v-if="overPickLimit">You have {{ picksMade }}. Remove {{ picksMade - 5 }} before sending.</template>
            <template v-else-if="picksMade < 5">
              Sends these {{ picksMade }} now — email the other {{ 5 - picksMade }} before the deadline.
            </template>
            <template v-else-if="senderEmail">
              Drafts to {{ picksRecipient }} as {{ senderEmail }} — you still send it.
            </template>
            <template v-else>Drafts to {{ picksRecipient }} for your review.</template>
          </p>
        </div>
      </div>
      <div class="mt-3 flex flex-wrap gap-2">
        <button
          v-for="choice in emailChoices"
          :key="choice.key"
          type="button"
          :disabled="!canSend"
          :class="[
            'shrink-0 rounded-lg px-3 py-1.5 text-sm font-bold transition-colors disabled:cursor-not-allowed disabled:bg-slate-700 disabled:text-slate-500',
            choice.primary
              ? 'bg-brand-green text-slate-900 hover:bg-emerald-400 active:bg-emerald-500'
              : 'border border-slate-600 bg-transparent text-slate-300 hover:bg-white/5 disabled:border-slate-700',
          ]"
          @click="choice.run()"
        >
          {{ choice.label }}
        </button>
      </div>
      <p v-if="emailNotice" class="mt-2 text-xs text-emerald-400">{{ emailNotice }}</p>
      <p class="mt-3 border-t border-white/10 pt-3 text-xs text-slate-400">
        <template v-if="submittedCount">
          {{ submittedCount }} of {{ pickedGames.length }} locked — on the commissioner's sheet.
        </template>
        <template v-else>They lock when the commissioner's All Picks email arrives.</template>
      </p>
    </div>

    <!-- Every game this week, graded against the frozen line as it goes --
         the same list as Results' week view, here so the page you sent your
         picks from is the one you watch them on. Refreshes itself while games
         are on (see liveTimer). -->
    <PicksGamesPanel
      v-if="!loading && weekGames.length"
      class="mt-4"
      :games="weekGames"
      :picks="weekPicks"
      :now="now"
      :crowd="crowd"
      :outcomes="outcomes"
    />
  </div>
</template>

<script>
import api from "@/utils/api";
import auth from "@/utils/auth";
import PickCard from "../components/PickCard.vue";
import GamedayPicks from "../components/GamedayPicks.vue";
import PicksGamesPanel from "../components/PicksGamesPanel.vue";
import PoolStandingCard from "../components/PoolStandingCard.vue";
import { gameOutcomes, liveStandings } from "../utils/poolCrowd.js";
import { gamedayPicks, gamedaySummary } from "../utils/gameday.js";
import { analyzeHistoryGame } from "../utils/history.js";
import { isFinal, yourWeekPicks } from "../utils/weekGames.js";
import { mapStoredGame } from "../utils/storedGame.js";
import {
  composePickEmail,
  gmailComposeUrl,
  gmailAppUrl,
  mailtoUrl,
  pickEmailText,
  isMobileDevice,
  PICKS_RECIPIENT,
} from "../utils/pickEmail.js";
import { recordEmailed } from "../utils/emailedPicks.js";

// Mirrors app/models/concerns/season_type.rb's SeasonType::WEEKS. There's no
// season-type picker here -- the pool's picks never go past regular season
// Week 18 -- but the backend can still resolve to preseason or postseason
// (Current.season_type isn't nudged, and Home lets you browse those weeks
// too), so every entry has to stay or the prev/next buttons would throw
// reading an undefined range.
const WEEK_RANGES = {
  preseason: [0, 4],
  regular: [1, 18],
  postseason: [1, 5],
};

export default {
  name: "PicksView",
  components: { PickCard, GamedayPicks, PicksGamesPanel, PoolStandingCard },
  data() {
    return {
      season: null,
      week: null,
      seasonType: "regular",
      games: [],
      loading: true,
      refreshing: false,
      error: null,
      pickError: null,
      skipNextRouteLoad: false,
      emailNotice: null,
      // The week's games as History reads them (deadline market, graded rows),
      // and the pool summary that says whose picks "My picks" can show.
      weekGames: [],
      pool: null,
      // The week's All Picks beside last week's standings (/pool_crowd),
      // fetched once per week: the sheet does not change while games do.
      crowd: null,
      crowdKey: null,
      liveTimer: null,
      // Read when the page last loaded, so kickoff labels and the live
      // readings agree with the data they sit beside.
      now: Date.now(),
    };
  },
  computed: {
    // Picks is a review surface now -- picking happens on Home, where the
    // edge analysis that justifies a side actually lives. Only the games
    // you've already locked in belong here.
    pickedGames() {
      return this.games.filter((game) => game.pick);
    },
    picksMade() {
      return this.pickedGames.length;
    },
    picksRecipient() {
      return PICKS_RECIPIENT;
    },
    // The Google account that signed in to this app. Pinning the Gmail compose
    // to it is what stops a multi-account browser drafting from the wrong one.
    senderEmail() {
      return auth.state.user?.email || null;
    },
    pickEmailFields() {
      return composePickEmail({
        week: this.week,
        pickerName: auth.state.user?.name,
        picks: this.pickedGames.map((game) =>
          game.pick.side === "home" ? game.homeTeam.name : game.awayTeam.name
        ),
      });
    },
    // Offered, not chosen for you. Every hand-off depends on device state this
    // app cannot see, so the picker picks; the UA only decides which one reads
    // first. "Copy" is last but is the one that cannot fail.
    emailChoices() {
      const gmail = {
        key: "gmail",
        label: "Open in Gmail",
        primary: true,
        run: () => this.openUrl(gmailComposeUrl(this.pickEmailFields, this.senderEmail), true),
      };
      const app = {
        key: "app",
        label: isMobileDevice() ? "Gmail app" : "Mail app",
        primary: false,
        run: () =>
          this.openUrl(
            isMobileDevice() ? gmailAppUrl(this.pickEmailFields) : mailtoUrl(this.pickEmailFields),
            false
          ),
      };
      const copy = {
        key: "copy",
        label: "Copy",
        primary: false,
        run: () => this.copyPickEmail(),
      };
      return isMobileDevice() ? [app, gmail, copy] : [gmail, app, copy];
    },
    atPickLimit() {
      return this.picksMade === 5;
    },
    // Any picks up to five can go. A picker often sends a few early on Sunday
    // and the rest just before 1:00pm, so each email carries what is picked
    // so far; only a sixth pick blocks, since the pool takes five.
    canSend() {
      return this.picksMade > 0 && !this.overPickLimit;
    },
    overPickLimit() {
      return this.picksMade > 5;
    },
    submittedCount() {
      return this.pickedGames.filter((game) => game.pick?.submitted_at).length;
    },
    allSubmitted() {
      return (
        this.pickedGames.length > 0 &&
        this.submittedCount === this.pickedGames.length
      );
    },
    // Nothing left to send: the commissioner's sheet has every pick, or a
    // pick has already been graded -- which it could only be once sent.
    sendingDone() {
      return this.allSubmitted || this.hasSettledResults;
    },
    // Always known here, unlike on History: these are the viewer's own app
    // picks. A linked account's sheet row, once one exists, is the record.
    weekPicks() {
      const weekRow = (this.pool?.weeks || []).find((row) => row.week === this.week) || null;
      return yourWeekPicks(this.weekGames, { identified: true, weekRow });
    },
    // A game past kickoff that ESPN has not called final -- the page is worth
    // re-reading while there is one. Five hours bounds a status that never
    // flips (the backend's RefreshLiveScores::LIVE_WINDOW).
    gamesInProgress() {
      const now = Date.now();
      return this.weekGames.some((game) => {
        if (isFinal(game) || !game.kickoff_at) return false;
        const kickoff = new Date(game.kickoff_at).getTime();
        return kickoff <= now && now - kickoff < 5 * 60 * 60 * 1000;
      });
    },
    gameday() {
      return gamedayPicks(this.pickedGames, this.weekGames, this.now);
    },
    outcomes() {
      return gameOutcomes(this.weekGames);
    },
    poolStandings() {
      if (!this.crowd?.available || !this.crowd.me?.identified) return null;
      return liveStandings(this.crowd, this.outcomes);
    },
    gamedaySummary() {
      return gamedaySummary(this.gameday);
    },
    // One of your own picks being played: the page is being watched, so it
    // re-reads every 20 seconds rather than every minute (the backend's
    // RefreshLiveScores::PICK_LIVE_THROTTLE matches).
    pickLive() {
      return this.gameday.some((pick) => pick.state === "live");
    },
    pollInterval() {
      if (this.pickLive) return 20 * 1000;
      return this.gamesInProgress ? 60 * 1000 : null;
    },
    // Nothing can be sent once every one of your picks' decisions has closed
    // -- the earlier of kickoff and Sunday 1:00pm ET, as the backend reports
    // it -- so the send card goes, even before the sheet has arrived.
    sendDeadlinePassed() {
      const ids = new Set(this.pickedGames.map((game) => game.id));
      const deadlines = this.weekGames
        .filter((game) => ids.has(game.id) && game.decision_deadline_at)
        .map((game) => new Date(game.decision_deadline_at).getTime());
      return deadlines.length > 0 && deadlines.length === ids.size && Math.max(...deadlines) <= this.now;
    },
    // Whether this week has graded picks at all.
    hasSettledResults() {
      return this.pickedGames.some((game) => game.pick && game.pick.result);
    },
    canGoPrevWeek() {
      return this.week != null && this.week > WEEK_RANGES[this.seasonType][0];
    },
    canGoNextWeek() {
      return this.week != null && this.week < WEEK_RANGES[this.seasonType][1];
    },
  },
  watch: {
    pollInterval: {
      handler(interval) {
        clearInterval(this.liveTimer);
        this.liveTimer = interval ? setInterval(this.refreshLive, interval) : null;
      },
    },
    "$route.query"() {
      if (this.skipNextRouteLoad) {
        this.skipNextRouteLoad = false;
        return;
      }
      this.load();
    },
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      const query = this.$route.query;
      const params = {};
      if (query.season) params.season = query.season;
      if (query.week) params.week = query.week;
      if (query.season_type) params.season_type = query.season_type;

      try {
        const response = await api.get("/api/v1/picks", { params });
        this.season = response.data.season;
        this.week = response.data.week;
        this.seasonType = response.data.season_type;
        this.games = (response.data.games || []).map(mapStoredGame);
        this.canonicalizeUrl();
        await this.loadWeekGames();
      } catch (err) {
        console.error("There was an error!", err);
        this.error =
          err.response?.data?.error || "Couldn't load this week's games.";
      } finally {
        this.loading = false;
      }
    },
    // A bare /picks load has no query string; once the backend has resolved a
    // real season/week/season_type, reflect it in the URL so the same slate
    // survives a reload and can be linked -- without triggering a second fetch
    // for the load that just finished.
    canonicalizeUrl() {
      const query = this.$route.query;
      const canonical = {
        season: String(this.season),
        week: String(this.week),
        season_type: this.seasonType,
      };
      if (
        query.season === canonical.season &&
        query.week === canonical.week &&
        query.season_type === canonical.season_type
      ) {
        return;
      }
      this.skipNextRouteLoad = true;
      this.$router.replace({ query: canonical });
    },
    goToWeek(delta) {
      const range = WEEK_RANGES[this.seasonType];
      const next = this.week + delta;
      if (next < range[0] || next > range[1]) return;
      this.$router.push({
        query: {season: String(this.season), week: String(next), season_type: this.seasonType},
      });
    },
    // Picks remains a review surface: changing sides happens on Home, while
    // the expanded row provides a deliberate way to remove a live pick.
    async removePick(game) {
      this.pickError = null;
      try {
        await api.delete(`/api/v1/picks/${game.pick.id}`);
        game.pick = null;
      } catch (err) {
        this.pickError =
          err.response?.data?.error || "Couldn't remove that pick. Try again.";
      }
    },
    // `newTab` only for the Gmail web compose, which is an ordinary page. A
    // `mailto:` or a custom scheme has to navigate in this tab -- a popup
    // opened for one is left behind blank, or blocked outright, once the
    // handing-off app takes over.
    openUrl(url, newTab) {
      if (!this.canSend) return;
      this.emailNotice = null;
      this.noteEmailed();
      if (newTab) window.open(url, "_blank", "noopener");
      else window.location.href = url;
    },
    // Home reads this to offer "Email update" if the five change afterwards.
    noteEmailed() {
      const teams = this.pickedGames.map((game) =>
        game.pick.side === "home" ? game.homeTeam.abbr : game.awayTeam.abbr
      );
      recordEmailed(this.season, this.seasonType, this.week, teams);
    },
    async copyPickEmail() {
      if (!this.canSend) return;
      const text = pickEmailText(this.pickEmailFields);
      try {
        // Absent outside a secure context, which includes plain-http LAN
        // testing on a phone -- the exact place this fallback matters most.
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          const area = document.createElement("textarea");
          area.value = text;
          area.setAttribute("readonly", "");
          area.style.position = "fixed";
          area.style.opacity = "0";
          document.body.appendChild(area);
          area.select();
          document.execCommand("copy");
          document.body.removeChild(area);
        }
        this.emailNotice = "Copied. Paste it into any mail app.";
        this.noteEmailed();
      } catch {
        this.emailNotice = "Couldn't copy — select the picks above and copy manually.";
      }
    },
    // History's read of this one week. Asking for a week is also what makes
    // the backend pull fresh ESPN scores while its games are on. A failure
    // here only hides the list -- the picks above still stand.
    async loadWeekGames() {
      try {
        const { data } = await api.get("/api/v1/history", {
          params: { season: this.season, week: this.week, season_type: this.seasonType },
        });
        this.weekGames = data.games.map(analyzeHistoryGame);
        this.pool = data.pool || null;
        this.now = Date.now();
      } catch (err) {
        console.error("Couldn't load the week's games", err);
        this.weekGames = [];
      }
      await this.loadCrowd();
    },
    // Re-asked until the sheet is in, then held for the week. A failure only
    // leaves the pool sections out.
    async loadCrowd() {
      const key = `${this.season}-${this.seasonType}-${this.week}`;
      if (this.crowdKey === key && this.crowd?.available) return;
      if (this.crowdKey !== key) this.crowd = null;
      try {
        const { data } = await api.get("/api/v1/pool_crowd", { params: { season: this.season, week: this.week, season_type: this.seasonType } });
        this.crowd = data;
        this.crowdKey = key;
      } catch (err) {
        console.error("Couldn't load the pool's picks", err);
      }
    },
    // Quiet re-read while games are on: no spinner, and skipped while the tab
    // is in the background so a phone left on this page doesn't poll all day.
    async refreshLive({ force = false } = {}) {
      if (!force && document.visibilityState === "hidden") return;
      try {
        const { data } = await api.get("/api/v1/picks", {
          params: { season: this.season, week: this.week, season_type: this.seasonType },
        });
        this.games = (data.games || []).map(mapStoredGame);
      } catch {
        // The next tick tries again; the page keeps what it had.
      }
      await this.loadWeekGames();
    },
    // The header's refresh button: the same quiet re-read, on demand.
    async refreshNow() {
      if (this.refreshing || this.week == null) return;
      this.refreshing = true;
      try {
        await this.refreshLive({ force: true });
      } finally {
        this.refreshing = false;
      }
    },
  },
  mounted() {
    this.load();
  },
  beforeUnmount() {
    clearInterval(this.liveTimer);
  },
};
</script>
